import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import './ChampionSearch.css';
import { useChampions, Champion } from '../hooks/useChampions';

interface ChampionSearchProps {
  onSelectChampion: (championName: string) => void;
  placeholder?: string;
}

const ChampionSearch: React.FC<ChampionSearchProps> = ({ 
  onSelectChampion, 
  placeholder = "Search for a champion..." 
}) => {
  const { searchChampions } = useChampions();
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<Champion[]>([]);
  const [selectedChampion, setSelectedChampion] = useState<Champion | null>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update suggestions when search input changes
  useEffect(() => {
    if (searchInput.length > 0) {
      const results = searchChampions(searchInput);
      setSuggestions(results);
      
      // Auto-select first suggestion if available
      if (results.length > 0) {
        setSelectedChampion(results[0]);
      } else {
        setSelectedChampion(null);
      }
    } else {
      setSuggestions([]);
      setSelectedChampion(null);
    }
  }, [searchInput, searchChampions]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setSearchInput(input);
    setIsDropdownVisible(true); // Show dropdown when typing
  };

  const handleInputFocus = () => {
    // Show dropdown when focusing if there are suggestions to show
    if (suggestions.length > 0) {
      setIsDropdownVisible(true);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && selectedChampion) {
      handleConfirmSelection();
    } else if (e.key === 'Escape') {
      // Close dropdown on Escape key
      setIsDropdownVisible(false);
      inputRef.current?.blur();
    }
  };

  const handleConfirmSelection = () => {
    if (selectedChampion) {
      onSelectChampion(selectedChampion.id);  // Send ID for URL safety
      // Clear the search after selection
      setSearchInput('');
      setSuggestions([]);
      setSelectedChampion(null);
      setIsDropdownVisible(false);
    }
  };

  const handleChampionSelect = (champion: Champion) => {
    setSelectedChampion(champion);
    setSearchInput(champion.name);
    setIsDropdownVisible(false);
    // Automatically confirm selection when clicking - send ID for URL safety
    onSelectChampion(champion.id);
    // Clear after a short delay for visual feedback
    setTimeout(() => {
      setSearchInput('');
      setSelectedChampion(null);
    }, 100);
  };

  return (
    <div className='landing-champion-search-wrapper' ref={wrapperRef}>
      <div className='landing-champion-search-bar'>
        <input
          ref={inputRef}
          type='text'
          placeholder={placeholder}
          value={searchInput}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="landing-champion-search-input"
        />
        
        {suggestions.length > 0 && isDropdownVisible && (
          <ul className='landing-champion-suggestions-list'>
            {suggestions.map((champion) => (
              <li
                key={champion.id}
                className={`landing-champion-suggestion-item ${selectedChampion?.id === champion.id ? 'selected' : ''}`}
                onClick={() => handleChampionSelect(champion)}
              >
                <img src={champion.imageUrl} alt={champion.name} />
                <span>{champion.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChampionSearch;
